import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Edit2, Save, Trash2, X, Plus, Notebook } from "lucide-react"
import ReactMarkdown from "react-markdown"
import { formatDistanceToNow } from "date-fns"
import { es, enUS } from "date-fns/locale"
import { toast } from "sonner"

interface Note {
    id: string
    content: string
    created_at: string
    updated_at: string
}

interface NotesSectionProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    notes: any[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSave: (content: string, id?: string) => Promise<any>
    onDelete: (id: string) => Promise<boolean>
    title?: string
    placeholder?: string
    isSaving?: boolean
}

export function NotesSection({
    notes,
    onSave,
    onDelete,
    title,
    placeholder,
    isSaving: externalIsSaving
}: NotesSectionProps) {
    const { t, i18n } = useTranslation()
    const [isAdding, setIsAdding] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [content, setContent] = useState("")
    const [internalIsSaving, setInternalIsSaving] = useState(false)

    const isSaving = externalIsSaving || internalIsSaving
    const dateLocale = i18n.resolvedLanguage?.startsWith('es') ? es : enUS
    const displayTitle = title || t('common.notes.title')
    const displayPlaceholder = placeholder || t('common.notes.placeholder')

    const handleSave = async (id?: string) => {
        if (!content.trim()) return

        setInternalIsSaving(true)
        try {
            const result = await onSave(content, id)
            if (result) {
                toast.success(id ? t('common.notes.updated') : t('common.notes.saved'))
                setContent("")
                setIsAdding(false)
                setEditingId(null)
            }
        } catch {
            toast.error(t('common.notes.errorSave'))
        } finally {
            setInternalIsSaving(false)
        }
    }

    const startEdit = (note: Note) => {
        setEditingId(note.id)
        setContent(note.content)
        setIsAdding(false)
    }

    const cancelEdit = () => {
        setEditingId(null)
        setIsAdding(false)
        setContent("")
    }

    const handleDelete = async (id: string) => {
        if (!confirm(t('common.notes.deleteConfirm'))) return

        const success = await onDelete(id)
        if (success) {
            toast.success(t('common.notes.deleted'))
        } else {
            toast.error(t('common.notes.errorDelete'))
        }
    }

    return (
        <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <Notebook className="w-5 h-5 text-primary" />
                    {displayTitle}
                </CardTitle>
                {!isAdding && !editingId && (
                    <Button size="sm" onClick={() => setIsAdding(true)} className="gap-2">
                        <Plus className="w-4 h-4" /> {t('common.notes.new')}
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {(isAdding || editingId) && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                        <Textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder={displayPlaceholder}
                            className="min-h-[150px] font-mono text-sm"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={isSaving}>
                                <X className="w-4 h-4 mr-1" /> {t('common.cancel')}
                            </Button>
                            <Button size="sm" onClick={() => handleSave(editingId || undefined)} disabled={!content.trim() || isSaving}>
                                {isSaving ? t('common.notes.saving') : <><Save className="w-4 h-4 mr-1" /> {t('common.save')}</>}
                            </Button>
                        </div>
                        {content.trim() && (
                            <div className="p-3 border border-border/50 rounded-md bg-zinc-50 dark:bg-muted/30">
                                <p className="text-xs font-semibold text-muted-foreground mb-2 whitespace-nowrap overflow-hidden">{t('common.notes.preview')}</p>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <ReactMarkdown>{content}</ReactMarkdown>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <ScrollArea className="h-full max-h-[500px] rounded-md">
                    {notes.length === 0 && !isAdding && (
                        <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground border-2 border-dashed rounded-lg">
                            <Notebook className="w-10 h-10 mb-2 opacity-20" />
                            <p className="text-sm">{t('common.notes.empty')}</p>
                            <Button variant="link" size="sm" onClick={() => setIsAdding(true)}>{t('common.notes.createFirst')}</Button>
                        </div>
                    )}

                    <div className="space-y-4">
                        {notes.map((note) => (
                            <div key={note.id} className="group relative p-4 border rounded-lg bg-card hover:border-primary/50 transition-colors">
                                {editingId === note.id ? (
                                    <p className="text-sm italic text-muted-foreground">{t('common.notes.editing')}</p>
                                ) : (
                                    <>
                                        <div className="flex items-start justify-between mb-2">
                                            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                                                {(() => {
                                                    const date = new Date(note.updated_at || note.created_at || new Date());
                                                    const isValid = !isNaN(date.getTime());
                                                    return formatDistanceToNow(isValid ? date : new Date(), { addSuffix: true, locale: dateLocale });
                                                })()}
                                            </span>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(note)}>
                                                    <Edit2 className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(note.id)}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="prose prose-sm dark:prose-invert max-w-none overflow-hidden">
                                            <ReactMarkdown>{note.content}</ReactMarkdown>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    )
}
